// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Clickable from "@insite/mobius/Clickable";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { MappedLink } from "@stoneage/MobiusOverrides/Menu/Menu";
import { fontConfig, isWarthog } from "@stoneage/XDL/constants";
import { color, extractPlainText, getCategoryAnchorId, styledScrollbar, xClassNames, xParserOptions } from "@stoneage/XDL/Utilities";
import useMenuLinkClick from "@stoneage/MobiusOverrides/Menu/useMenuLinkClick";
import { useId } from "@floating-ui/react";
import React, { FC, memo } from "react";
import styled, { css } from "styled-components";
import parse from "html-react-parser";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import translate from "@insite/client-framework/Translate";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";

export const MegaMenuStyle = styled.ul<{ referenceIndex?: number; currentDepth?: number }>`
    display: grid;
    grid-template-columns: 480px 240px;
    background: ${getColor("common.white")};
    z-index: ${getProp("theme.zIndex.menu")};
    border: 1px solid ${color("common.ui6")};
    border-left: 0;
    height: 100%;
    list-style: none;
    gap: 0;

    ${injectCss}
`;

export interface MegaMenuStyles {
    categoriesContainer?: InjectableCss;
    categoriesInnerContainer?: InjectableCss;
    columnContainer?: InjectableCss;
    categoryTitle?: TypographyPresentationProps;
    categoryLink?: LinkPresentationProps;
    promoContainer?: InjectableCss;
    promoImage?: LazyImageProps;
    promoImagePlaceholder?: InjectableCss;
    promoBody?: TypographyPresentationProps;
}

export const styles: MegaMenuStyles = {
    categoriesContainer: {
        css: css`
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
            align-content: start;
            overflow-y: auto;
            overflow-x: hidden;
            ${styledScrollbar(4)}
        `,
    },
    categoriesInnerContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            max-width: 240px;
        `,
    },
    columnContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 12px;
            min-width: 180px;
            padding: 12px;
        `,
    },
    categoryTitle: {
        ...fontConfig.smallSingleLine,
        weight: fontConfig.weight.medium,
        color: "secondary",
        css: css`
            display: block;
            padding: 0 0 8px 0;
            border-bottom: 1px solid ${getColor("common.border")};
        `,
    },
    categoryLink: {
        color: "secondary",
        typographyProps: {
            ...fontConfig.xSmallSingleLine,
        },
        css: css`
            align-self: baseline;
        `,
    },
    promoContainer: {
        css: css`
            height: 100%;
            padding: 12px;
            display: flex;
            flex-direction: column;
            gap: 12px;
            overflow: hidden;
            &:not(.transparent) {
                background-color: ${getColor("common.ui4")};
            }
        `,
    },
    promoImage: {
        width: "100%",
        height: "121px",
        css: css`
            img {
                object-fit: cover;
                flex-shrink: 0;
            }
        `,
    },
    promoImagePlaceholder: {
        css: css`
            width: 100%;
            height: 121px;
            flex-shrink: 0;
            &:not(.transparent) {
                background-color: ${getColor("common.ui4")};
            }
        `,
    },
    promoBody: {
        ...fontConfig.small,
        weight: fontConfig.weight.regular,
        forwardAs: "div",
        css: css`
            p {
                font-size: inherit;
                line-height: inherit;
                font-weight: inherit;

                &:last-child {
                    margin-bottom: 0;
                }
            }
        `,
    },
};

const normalizePath = (path: string) => path.split(/[?#]/)[0].replace(/\/+$/, "") || "/";

/** Last-resort parent derivation when the panel wasn't given its owner's url. */
const getParentUrl = (url: string) => {
    const path = normalizePath(url);
    const parent = path.slice(0, path.lastIndexOf("/"));
    return parent || "/";
};

/**
 * XNG-Changes: "View All" targets the parent category page and deep-links to this
 * category's section (`/parent#category-link-<categoryId>`), which the category
 * page scrolls to.
 *
 * `parentUrl` is threaded down from the menu item that owns this panel — deriving
 * it by trimming the last url segment breaks for single-segment category paths,
 * where it resolves to "/" and sends the user to the home page.
 *
 * Falls back to the item's own url on warthog, when there is no category id
 * (custom links), or when no usable parent page could be determined.
 */
const getViewAllHref = (item: MappedLink, parentUrl?: string) => {
    const anchorId = getCategoryAnchorId(item.categoryId);

    if (isWarthog || !item.url || !anchorId) {
        return item.url;
    }

    const resolvedParent = normalizePath(parentUrl || getParentUrl(item.url));

    if (resolvedParent === "/" || resolvedParent === normalizePath(item.url)) {
        return item.url;
    }

    return `${resolvedParent}#${anchorId}`;
};

export interface RenderMegaMenuItemsProps {
    childMenuItems?: MappedLink[];
    navigationImage?: string;
    navigationContent?: string;
    /** XNG-Changes: url of the menu item that owns this panel — the "View All" target page. */
    parentUrl?: string;
}

export const RenderMegaMenuItems: FC<RenderMegaMenuItemsProps> = memo(
    ({ childMenuItems, navigationImage, navigationContent, parentUrl }) => {
        const id = useId();
        const handleMenuLinkClick = useMenuLinkClick();

        if (!childMenuItems) {
            return null;
        }

        const visibleItems = childMenuItems.filter(item => !item.excludeFromNavigation);

        // XNG-Changes: split L2 items into explicit columns based on navigationColumn.
        // Items without navigationColumn default to "Column 1".
        const col1Items = visibleItems.filter(
            item => !item.properties?.navigationColumn || item.properties?.navigationColumn === "Column 1",
        );
        const col2Items = visibleItems.filter(item => item.properties?.navigationColumn === "Column 2");

        /**
         * Renders a list of L2 items as stacked section blocks.
         * Handles navigationMaxLinks capping and the "View All" link per section.
         */
        const renderColumnItems = (items: MappedLink[], columnKey: string) =>
            items.map((item, colIndex) => {
                const allLinks = item.children?.filter(child => !child.excludeFromNavigation) ?? [];
                const hasMore =
                    item.properties?.navigationMaxLinks !== undefined &&
                    allLinks.length > item.properties?.navigationMaxLinks;
                const visibleLinks = hasMore ? allLinks.slice(0, item.properties?.navigationMaxLinks) : allLinks;
                const viewAllHref = getViewAllHref(item, parentUrl);
                const viewAllAnchorId = viewAllHref?.includes("#") ? getCategoryAnchorId(item.categoryId) : undefined;

                return (
                    <StyledWrapper {...styles.columnContainer} key={`${columnKey}_${colIndex}`}>
                        {/* L2 column header */}
                        <Typography {...styles.categoryTitle}>
                            {item.isNonClickable || !item.url ? (
                                item.title
                            ) : (
                                <Clickable
                                    href={item.url}
                                    target={item.openInNewWindow ? "_blank" : ""}
                                    {...item.clickableProps}
                                    onClick={(event: React.MouseEvent<HTMLElement>) =>
                                        handleMenuLinkClick(event, {
                                            href: item.url,
                                            openInNewWindow: item.openInNewWindow,
                                        })
                                    }
                                    aria-label={item.title}
                                >
                                    {item.title}
                                </Clickable>
                            )}
                        </Typography>

                        {/* L3 links — capped to navigationMaxLinks */}
                        {visibleLinks.map((child, childIndex) => (
                            <Link
                                href={child.url}
                                target={child.openInNewWindow ? "_blank" : ""}
                                onClick={(event: React.MouseEvent<HTMLElement>) =>
                                    handleMenuLinkClick(event, {
                                        href: child.url,
                                        openInNewWindow: child.openInNewWindow,
                                    })
                                }
                                aria-label={child.title}
                                key={`child_${columnKey}_${colIndex}_${childIndex}`}
                                {...styles.categoryLink}
                            >
                                {child.title}
                            </Link>
                        ))}
                        {hasMore && item.url && (
                            <Link
                                href={viewAllHref}
                                onClick={(event: React.MouseEvent<HTMLElement>) =>
                                    handleMenuLinkClick(event, {
                                        href: viewAllHref,
                                        anchorId: viewAllAnchorId,
                                        openInNewWindow: item.openInNewWindow,
                                    })
                                }
                                target={item.openInNewWindow ? "_blank" : ""}
                                aria-label={`View More ${item.title}`}
                                {...styles.categoryLink}
                            >
                                {translate("View All")}
                            </Link>
                        )}
                    </StyledWrapper>
                );
            });

        const isTransparentBackground = !navigationImage && !navigationContent;
        return (
            <React.Fragment key={id}>
                <StyledWrapper {...styles.categoriesContainer}>
                    <StyledWrapper {...styles.categoriesInnerContainer}>
                        {renderColumnItems(col1Items, "col1")}
                    </StyledWrapper>
                    <StyledWrapper {...styles.categoriesInnerContainer}>
                        {renderColumnItems(col2Items, "col2")}
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper
                    {...styles.promoContainer}
                    className={xClassNames.append(isTransparentBackground && "transparent")}
                >
                    {navigationImage ? (
                        <LazyImage
                            src={navigationImage}
                            altText={extractPlainText(navigationContent || "")}
                            {...styles.promoImage}
                        />
                    ) : (
                        <StyledWrapper
                            {...styles.promoImagePlaceholder}
                            className={xClassNames.append(isTransparentBackground && "transparent")}
                        />
                    )}
                    {navigationContent && (
                        <Typography {...styles.promoBody}>{parse(navigationContent, xParserOptions)}</Typography>
                    )}
                </StyledWrapper>
            </React.Fragment>
        );
    },
);
